/**
 * Procurement API service
 */
import axiosInstance from '../../../shared/api/axiosInstance';
import type {
  VendorProfile,
  VendorCreate,
  VendorUpdate,
  ListVendorsParams,
  VendorRowListResponse,
  BulkCreateProfilesRequest,
  BulkCreateProfilesResponse,
  PurchaseRequisition,
  RequisitionCreate,
  RequisitionUpdate,
  ConvertRequisitionRequest,
  ListRequisitionsParams,
  PurchaseOrder,
  ListVendorOrdersParams,
  VendorOrderListResponse,
  PurchaseOrderCreate,
  PurchaseOrderUpdate,
  PurchaseOrderEmailRequest,
  InstallmentPrepayRequest,
  InstallmentPrepayResult,
  ListPurchaseOrdersParams,
  GoodsReceipt,
  GoodsReceiptCreate,
  ReceiptReasonCode,
  UnmatchedReceiptReasonCode,
  ListGoodsReceiptsParams,
  LicensePlate,
  LicensePlateCreate,
  LicensePlateStatus,
  ThreeWayMatchReport,
  LandedCostRollup,
  LandedCostApplyResult,
  SupplierClaim,
  SupplierClaimCreate,
  SupplierClaimUpdate,
  SupplierClaimTransition,
  ListSupplierClaimsParams,
  VendorReturn,
  VendorReturnCreate,
  VendorReturnUpdate,
  CreateVendorCreditResult,
  ListVendorReturnsParams,
  ProcurementDashboard,
  ProcurementTenantSettings,
  RecurringPoTemplate,
  RecurringPoTemplateCreate,
  RecurringPoTemplateUpdate,
  ListRecurringPoTemplatesParams,
  OverdueReport,
  AnticipatedReceiptsReport,
  PoHistoryReport,
  PurchasesReport,
  ReportDateRangeParams,
  Rfq,
  RfqCreate,
  RfqUpdate,
  RfqFromSalesOrderRequest,
  RfqSupplierGroup,
  RfqResponseState,
  ListRfqsParams,
  SupplierPi,
  RfqComparison,
} from '../types';

const BASE_URL = '/api/v1/procurement';

// ─── Vendor API ────────────────────────────────────────────────

export const vendorApi = {
  list: async (params?: ListVendorsParams): Promise<VendorRowListResponse> => {
    const response = await axiosInstance.get(`${BASE_URL}/vendors`, { params });
    return response.data as VendorRowListResponse;
  },

  get: async (id: string): Promise<VendorProfile> => {
    const response = await axiosInstance.get(`${BASE_URL}/vendors/${id}`);
    return response.data.data;
  },

  create: async (data: VendorCreate): Promise<VendorProfile> => {
    const response = await axiosInstance.post(`${BASE_URL}/vendors`, data);
    return response.data.data;
  },

  bulkCreateProfiles: async (
    data: BulkCreateProfilesRequest,
  ): Promise<BulkCreateProfilesResponse> => {
    const response = await axiosInstance.post(`${BASE_URL}/vendors/bulk-create-profiles`, data);
    return response.data as BulkCreateProfilesResponse;
  },

  update: async (id: string, data: VendorUpdate): Promise<VendorProfile> => {
    const response = await axiosInstance.patch(`${BASE_URL}/vendors/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/vendors/${id}`);
  },

  // Takes the vendor *profile* id; the backend resolves its organization_id (the
  // handle `proc_purchase_orders.vendor_id` actually keys on) and applies the
  // vendor's org-visibility gate. Server-side paginated — a vendor's PO history
  // grows without bound, so the endpoint always applies a limit.
  listOrders: async (
    id: string,
    params?: ListVendorOrdersParams,
  ): Promise<VendorOrderListResponse> => {
    const response = await axiosInstance.get(`${BASE_URL}/vendors/${id}/orders`, { params });
    return response.data;
  },
};

// ─── Requisition API ───────────────────────────────────────────

export const requisitionApi = {
  list: async (params?: ListRequisitionsParams): Promise<PurchaseRequisition[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/requisitions`, { params });
    return response.data.data;
  },

  get: async (id: string): Promise<PurchaseRequisition> => {
    const response = await axiosInstance.get(`${BASE_URL}/requisitions/${id}`);
    return response.data.data;
  },

  create: async (data: RequisitionCreate): Promise<PurchaseRequisition> => {
    const response = await axiosInstance.post(`${BASE_URL}/requisitions`, data);
    return response.data.data;
  },

  update: async (id: string, data: RequisitionUpdate): Promise<PurchaseRequisition> => {
    const response = await axiosInstance.patch(`${BASE_URL}/requisitions/${id}`, data);
    return response.data.data;
  },

  submit: async (id: string): Promise<PurchaseRequisition> => {
    const response = await axiosInstance.post(`${BASE_URL}/requisitions/${id}/submit`);
    return response.data.data;
  },

  approve: async (id: string): Promise<PurchaseRequisition> => {
    const response = await axiosInstance.post(`${BASE_URL}/requisitions/${id}/approve`);
    return response.data.data;
  },

  reject: async (id: string): Promise<PurchaseRequisition> => {
    const response = await axiosInstance.post(`${BASE_URL}/requisitions/${id}/reject`);
    return response.data.data;
  },

  convertToPO: async (
    id: string,
    payload: ConvertRequisitionRequest
  ): Promise<PurchaseOrder> => {
    const response = await axiosInstance.post(
      `${BASE_URL}/requisitions/${id}/convert`,
      payload
    );
    return response.data.data;
  },
};

// ─── Purchase Order API ────────────────────────────────────────

export const purchaseOrderApi = {
  list: async (params?: ListPurchaseOrdersParams): Promise<PurchaseOrder[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/orders`, { params });
    return response.data.data;
  },

  get: async (id: string): Promise<PurchaseOrder> => {
    const response = await axiosInstance.get(`${BASE_URL}/orders/${id}`);
    return response.data.data;
  },

  create: async (data: PurchaseOrderCreate): Promise<PurchaseOrder> => {
    const response = await axiosInstance.post(`${BASE_URL}/orders`, data);
    return response.data.data;
  },

  update: async (id: string, data: PurchaseOrderUpdate): Promise<PurchaseOrder> => {
    const response = await axiosInstance.patch(`${BASE_URL}/orders/${id}`, data);
    return response.data.data;
  },

  approve: async (id: string): Promise<PurchaseOrder> => {
    const response = await axiosInstance.post(`${BASE_URL}/orders/${id}/approve`);
    return response.data.data;
  },

  send: async (id: string): Promise<PurchaseOrder> => {
    const response = await axiosInstance.post(`${BASE_URL}/orders/${id}/send`);
    return response.data.data;
  },

  cancel: async (id: string): Promise<PurchaseOrder> => {
    const response = await axiosInstance.post(`${BASE_URL}/orders/${id}/cancel`);
    return response.data.data;
  },

  close: async (id: string): Promise<PurchaseOrder> => {
    const response = await axiosInstance.post(`${BASE_URL}/orders/${id}/close`);
    return response.data.data;
  },

  copy: async (id: string): Promise<PurchaseOrder> => {
    const response = await axiosInstance.post(`${BASE_URL}/orders/${id}/copy`);
    return response.data.data;
  },

  releaseBlanket: async (id: string): Promise<PurchaseOrder> => {
    const response = await axiosInstance.post(`${BASE_URL}/orders/${id}/release-blanket`);
    return response.data.data;
  },

  stageVoucher: async (id: string): Promise<{ id: string }> => {
    const response = await axiosInstance.post(`${BASE_URL}/orders/${id}/stage-voucher`);
    return response.data.data;
  },

  email: async (id: string, data: PurchaseOrderEmailRequest): Promise<void> => {
    await axiosInstance.post(`${BASE_URL}/orders/${id}/email`, data);
  },

  prepayInstallment: async (
    orderId: string,
    installmentId: string,
    data: InstallmentPrepayRequest
  ): Promise<InstallmentPrepayResult> => {
    const response = await axiosInstance.post(
      `${BASE_URL}/orders/${orderId}/installments/${installmentId}/prepay`,
      data
    );
    return response.data.data;
  },

  downloadPdf: async (id: string, poNumber: string): Promise<void> => {
    const response = await axiosInstance.get(`${BASE_URL}/orders/${id}/pdf`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${poNumber.replace(/\//g, '-')}.pdf`;
    a.click();
    window.URL.revokeObjectURL(url);
  },

  openPreview: async (id: string): Promise<void> => {
    const response = await axiosInstance.get(`${BASE_URL}/orders/${id}/preview`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/html' }));
    window.open(url, '_blank', 'noopener,noreferrer');
  },
};

// ─── RFQ API ───────────────────────────────────────────────────

export const rfqApi = {
  list: async (params?: ListRfqsParams): Promise<Rfq[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/rfqs`, { params });
    return response.data.data;
  },

  get: async (id: string): Promise<Rfq> => {
    const response = await axiosInstance.get(`${BASE_URL}/rfqs/${id}`);
    return response.data.data;
  },

  create: async (data: RfqCreate): Promise<Rfq> => {
    const response = await axiosInstance.post(`${BASE_URL}/rfqs`, data);
    return response.data.data;
  },

  createFromSalesOrder: async (data: RfqFromSalesOrderRequest): Promise<Rfq> => {
    const response = await axiosInstance.post(`${BASE_URL}/rfqs/from-sales-order`, data);
    return response.data.data;
  },

  update: async (id: string, data: RfqUpdate): Promise<Rfq> => {
    const response = await axiosInstance.put(`${BASE_URL}/rfqs/${id}`, data);
    return response.data.data;
  },

  setSuppliers: async (id: string, suppliers: RfqSupplierGroup[]): Promise<Rfq> => {
    const response = await axiosInstance.post(`${BASE_URL}/rfqs/${id}/suppliers`, { suppliers });
    return response.data.data;
  },

  send: async (id: string): Promise<Rfq> => {
    const response = await axiosInstance.post(`${BASE_URL}/rfqs/${id}/send`);
    return response.data.data;
  },

  cancel: async (id: string): Promise<Rfq> => {
    const response = await axiosInstance.post(`${BASE_URL}/rfqs/${id}/cancel`);
    return response.data.data;
  },

  recordSupplierResponse: async (
    id: string,
    rfqSupplierId: string,
    responseState: RfqResponseState,
  ): Promise<Rfq> => {
    const response = await axiosInstance.post(
      `${BASE_URL}/rfqs/${id}/suppliers/${rfqSupplierId}/response`,
      { response_state: responseState },
    );
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/rfqs/${id}`);
  },
};

// ─── Supplier PI (proforma invoice) & comparison API ([OC&CO] A4) ──────────

export const supplierPiApi = {
  comparison: async (rfqId: string): Promise<RfqComparison> => {
    const response = await axiosInstance.get(`${BASE_URL}/rfqs/${rfqId}/comparison`);
    return response.data.data;
  },

  get: async (piId: string): Promise<SupplierPi> => {
    const response = await axiosInstance.get(`${BASE_URL}/supplier-pis/${piId}`);
    return response.data.data;
  },

  selectWinner: async (rfqId: string, rfqLineId: string, piLineId: string): Promise<Rfq> => {
    const response = await axiosInstance.post(
      `${BASE_URL}/rfqs/${rfqId}/lines/${rfqLineId}/winner`,
      { pi_line_id: piLineId },
    );
    return response.data.data;
  },

  clearWinner: async (rfqId: string, rfqLineId: string): Promise<Rfq> => {
    const response = await axiosInstance.delete(
      `${BASE_URL}/rfqs/${rfqId}/lines/${rfqLineId}/winner`,
    );
    return response.data.data;
  },
};

// ─── Recurring PO Template API ─────────────────────────────────

export const recurringPoTemplateApi = {
  list: async (params?: ListRecurringPoTemplatesParams): Promise<RecurringPoTemplate[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/recurring-po-templates`, { params });
    return response.data.data;
  },

  get: async (id: string): Promise<RecurringPoTemplate> => {
    const response = await axiosInstance.get(`${BASE_URL}/recurring-po-templates/${id}`);
    return response.data.data;
  },

  create: async (data: RecurringPoTemplateCreate): Promise<RecurringPoTemplate> => {
    const response = await axiosInstance.post(`${BASE_URL}/recurring-po-templates`, data);
    return response.data.data;
  },

  update: async (id: string, data: RecurringPoTemplateUpdate): Promise<RecurringPoTemplate> => {
    const response = await axiosInstance.patch(`${BASE_URL}/recurring-po-templates/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/recurring-po-templates/${id}`);
  },

  run: async (id: string): Promise<PurchaseOrder> => {
    const response = await axiosInstance.post(`${BASE_URL}/recurring-po-templates/${id}/run`);
    return response.data.data;
  },
};

// ─── Reports API ───────────────────────────────────────────────

export const procurementReportApi = {
  overdue: async (asOf?: string): Promise<OverdueReport> => {
    const response = await axiosInstance.get(`${BASE_URL}/reports/overdue`, {
      params: asOf ? { as_of: asOf } : undefined,
    });
    return response.data.data;
  },

  anticipatedReceipts: async (params?: ReportDateRangeParams): Promise<AnticipatedReceiptsReport> => {
    const response = await axiosInstance.get(`${BASE_URL}/reports/anticipated-receipts`, { params });
    return response.data.data;
  },

  poHistory: async (params?: ReportDateRangeParams & { vendor_id?: string }): Promise<PoHistoryReport> => {
    const response = await axiosInstance.get(`${BASE_URL}/reports/po-history`, { params });
    return response.data.data;
  },

  purchases: async (params?: ReportDateRangeParams): Promise<PurchasesReport> => {
    const response = await axiosInstance.get(`${BASE_URL}/reports/purchases`, { params });
    return response.data.data;
  },
};

// ─── Goods Receipt API ─────────────────────────────────────────

export const goodsReceiptApi = {
  list: async (params?: ListGoodsReceiptsParams): Promise<GoodsReceipt[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/receipts`, { params });
    return response.data.data;
  },

  get: async (id: string): Promise<GoodsReceipt> => {
    const response = await axiosInstance.get(`${BASE_URL}/receipts/${id}`);
    return response.data.data;
  },

  create: async (data: GoodsReceiptCreate): Promise<GoodsReceipt> => {
    const response = await axiosInstance.post(`${BASE_URL}/receipts`, data);
    return response.data.data;
  },

  receive: async (id: string, confirmOverReceipt = false): Promise<GoodsReceipt> => {
    const response = await axiosInstance.post(
      `${BASE_URL}/receipts/${id}/receive`,
      undefined,
      { params: { confirm_over_receipt: confirmOverReceipt } },
    );
    return response.data.data;
  },

  cancel: async (id: string): Promise<GoodsReceipt> => {
    const response = await axiosInstance.post(`${BASE_URL}/receipts/${id}/cancel`);
    return response.data.data;
  },

  update: async (
    id: string,
    data: Partial<GoodsReceiptCreate> & Record<string, unknown>,
  ): Promise<GoodsReceipt> => {
    const response = await axiosInstance.patch(`${BASE_URL}/receipts/${id}`, data);
    return response.data.data;
  },

  downloadTicketPdf: async (id: string, receiptNumber: string): Promise<void> => {
    const response = await axiosInstance.get(`${BASE_URL}/receipts/${id}/pdf`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `Receiving-Ticket-${receiptNumber.replace(/\//g, '-')}.pdf`;
    a.click();
    window.URL.revokeObjectURL(url);
  },

  downloadLpLabels: async (id: string, receiptNumber: string): Promise<void> => {
    const response = await axiosInstance.get(`${BASE_URL}/receipts/${id}/labels`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `LP-Labels-${receiptNumber.replace(/\//g, '-')}.pdf`;
    a.click();
    window.URL.revokeObjectURL(url);
  },

  // Inventory inbound wizard — receive stock with no matching PO.
  createUnmatched: async (data: UnmatchedReceiptCreate): Promise<GoodsReceipt> => {
    const response = await axiosInstance.post(`${BASE_URL}/receipts/unmatched`, data);
    return response.data.data;
  },

  // Reconcile an unmatched receipt to a real PO.
  linkToPurchaseOrder: async (
    receiptId: string,
    purchaseOrderId: string,
  ): Promise<GoodsReceipt> => {
    const response = await axiosInstance.post(`${BASE_URL}/receipts/${receiptId}/link`, {
      purchase_order_id: purchaseOrderId,
    });
    return response.data.data;
  },
};

export interface UnmatchedReceiptItem {
  product_id: string;
  variant_id?: string | null;
  quantity_received: number;
  unit_cost?: number;
  lot_id?: string | null;
  lot_code?: string | null;
  lot_expiry_date?: string | null;
  location_id?: string | null;
  packaging_id?: string | null;
  quantity_in_packaging?: number | null;
  description?: string | null;
  notes?: string | null;
}

export interface UnmatchedReceiptCreate {
  warehouse_id: string;
  vendor_id: string;
  company_id: string;
  reason_code: UnmatchedReceiptReasonCode;
  notes?: string | null;
  received_date?: string | null;
  items: UnmatchedReceiptItem[];
}

// ─── License Plate API (T6) ────────────────────────────────────

export const licensePlateApi = {
  list: async (params?: {
    purchase_order_id?: string;
    goods_receipt_id?: string;
    status?: LicensePlateStatus;
    lp_number?: string;
  }): Promise<LicensePlate[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/license-plates`, { params });
    return response.data.data;
  },

  get: async (id: string): Promise<LicensePlate> => {
    const response = await axiosInstance.get(`${BASE_URL}/license-plates/${id}`);
    return response.data.data;
  },

  create: async (data: LicensePlateCreate): Promise<LicensePlate> => {
    const response = await axiosInstance.post(`${BASE_URL}/license-plates`, data);
    return response.data.data;
  },

  bulkCreate: async (items: LicensePlateCreate[]): Promise<LicensePlate[]> => {
    const response = await axiosInstance.post(`${BASE_URL}/license-plates/bulk`, items);
    return response.data.data;
  },

  update: async (id: string, data: Partial<LicensePlateCreate>): Promise<LicensePlate> => {
    const response = await axiosInstance.patch(`${BASE_URL}/license-plates/${id}`, data);
    return response.data.data;
  },

  verify: async (
    id: string,
    status: LicensePlateStatus = 'verified',
    notes?: string,
  ): Promise<LicensePlate> => {
    const response = await axiosInstance.post(`${BASE_URL}/license-plates/${id}/verify`, null, {
      params: { status, notes },
    });
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/license-plates/${id}`);
  },

  downloadLabel: async (id: string, lpNumber: string): Promise<void> => {
    const response = await axiosInstance.get(`${BASE_URL}/license-plates/${id}/label`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `LP-${lpNumber.replace(/\//g, '-')}.pdf`;
    a.click();
    window.URL.revokeObjectURL(url);
  },
};

// ─── Three-way match API (T6) ──────────────────────────────────

export const threeWayMatchApi = {
  forPo: async (poId: string): Promise<ThreeWayMatchReport> => {
    const response = await axiosInstance.get(`${BASE_URL}/orders/${poId}/three-way-match`);
    return response.data.data;
  },
};

// ─── AP bill from goods receipt (P2P-1) ───────────────────────
// Builds the vendor bill from a confirmed receipt. The bill is an accounting
// entity, reached here via the accounting REST endpoint (the sanctioned
// cross-module path) rather than a TS import — keeping the frontend module
// boundary intact. Returns the new draft bill id for navigation.
export const apBillFromReceiptApi = {
  create: async (goodsReceiptId: string): Promise<{ id: string }> => {
    const response = await axiosInstance.post('/api/v1/accounting/bills/from-receipt', {
      goods_receipt_id: goodsReceiptId,
    });
    return response.data.data;
  },
};

// ─── Dashboard API ─────────────────────────────────────────────

export const dashboardApi = {
  // On the shared KPI substrate: the response body IS the model — no {data: ...}
  // envelope to unwrap (docs/platform/dashboard-kpi-endpoints.md §1).
  get: async (): Promise<ProcurementDashboard> => {
    const response = await axiosInstance.get(`${BASE_URL}/dashboard/stats`);
    return response.data;
  },
};

// ─── Settings API ──────────────────────────────────────────────

export const settingsApi = {
  getTenantSettings: async (): Promise<ProcurementTenantSettings> => {
    const response = await axiosInstance.get(`${BASE_URL}/settings/tenant`);
    // Settings routes return the settings object flat (not the module's
    // {data: ...} envelope) — matches the inventory/support settings convention.
    return response.data;
  },

  updateTenantSettings: async (data: Partial<ProcurementTenantSettings>): Promise<ProcurementTenantSettings> => {
    const response = await axiosInstance.put(`${BASE_URL}/settings/tenant`, data);
    return response.data;
  },
};

// ─── Landed Cost API (T20C) ────────────────────────────────────

export const landedCostApi = {
  forOrder: async (orderId: string): Promise<LandedCostRollup> => {
    const response = await axiosInstance.get(`${BASE_URL}/orders/${orderId}/landed-cost`);
    return response.data.data;
  },

  forReceipt: async (receiptId: string): Promise<LandedCostRollup> => {
    const response = await axiosInstance.get(`${BASE_URL}/receipts/${receiptId}/landed-cost`);
    return response.data.data;
  },

  applyForOrder: async (orderId: string): Promise<LandedCostApplyResult> => {
    const response = await axiosInstance.post(`${BASE_URL}/orders/${orderId}/landed-cost/apply`);
    return response.data.data;
  },

  applyForReceipt: async (receiptId: string): Promise<LandedCostApplyResult> => {
    const response = await axiosInstance.post(`${BASE_URL}/receipts/${receiptId}/landed-cost/apply`);
    return response.data.data;
  },
};

// ─── Supplier Claims API (T20C) ────────────────────────────────

export const supplierClaimApi = {
  list: async (params?: ListSupplierClaimsParams): Promise<SupplierClaim[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/claims`, { params });
    return response.data.data;
  },

  get: async (id: string): Promise<SupplierClaim> => {
    const response = await axiosInstance.get(`${BASE_URL}/claims/${id}`);
    return response.data.data;
  },

  create: async (data: SupplierClaimCreate): Promise<SupplierClaim> => {
    const response = await axiosInstance.post(`${BASE_URL}/claims`, data);
    return response.data.data;
  },

  update: async (id: string, data: SupplierClaimUpdate): Promise<SupplierClaim> => {
    const response = await axiosInstance.patch(`${BASE_URL}/claims/${id}`, data);
    return response.data.data;
  },

  transition: async (id: string, data: SupplierClaimTransition): Promise<SupplierClaim> => {
    const response = await axiosInstance.post(`${BASE_URL}/claims/${id}/transition`, data);
    return response.data.data;
  },

  deduct: async (id: string): Promise<SupplierClaim> => {
    const response = await axiosInstance.post(`${BASE_URL}/claims/${id}/deduct`);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/claims/${id}`);
  },
};

// ─── Vendor Returns API (RTV, SEC-2) ───────────────────────────

export const vendorReturnApi = {
  list: async (params?: ListVendorReturnsParams): Promise<VendorReturn[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/vendor-returns/`, { params });
    return response.data.items ?? [];
  },

  get: async (id: string): Promise<VendorReturn> => {
    const response = await axiosInstance.get(`${BASE_URL}/vendor-returns/${id}`);
    return response.data.data;
  },

  create: async (data: VendorReturnCreate): Promise<VendorReturn> => {
    const response = await axiosInstance.post(`${BASE_URL}/vendor-returns/`, data);
    return response.data.data;
  },

  update: async (id: string, data: VendorReturnUpdate): Promise<VendorReturn> => {
    const response = await axiosInstance.patch(`${BASE_URL}/vendor-returns/${id}`, data);
    return response.data.data;
  },

  remove: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/vendor-returns/${id}`);
  },

  authorise: async (id: string): Promise<VendorReturn> => {
    const response = await axiosInstance.post(`${BASE_URL}/vendor-returns/${id}/authorise`);
    return response.data.data;
  },

  ship: async (id: string): Promise<VendorReturn> => {
    const response = await axiosInstance.post(`${BASE_URL}/vendor-returns/${id}/ship`);
    return response.data.data;
  },

  close: async (id: string): Promise<VendorReturn> => {
    const response = await axiosInstance.post(`${BASE_URL}/vendor-returns/${id}/close`);
    return response.data.data;
  },

  cancel: async (id: string): Promise<VendorReturn> => {
    const response = await axiosInstance.post(`${BASE_URL}/vendor-returns/${id}/cancel`);
    return response.data.data;
  },

  createVendorCredit: async (id: string): Promise<CreateVendorCreditResult> => {
    const response = await axiosInstance.post(
      `${BASE_URL}/vendor-returns/${id}/create-vendor-credit`,
    );
    return { vendorReturn: response.data.data, bill: response.data.bill };
  },
};

// ─── Receipt Reason Codes API ──────────────────────────────────

export const receiptReasonCodeApi = {
  list: async (kind?: 'reject' | 'damage'): Promise<ReceiptReasonCode[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/receipt-reason-codes`, {
      params: kind ? { kind } : undefined,
    });
    return response.data.data;
  },
};
